import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2 } from 'lucide-react'
import { apiClient, ApiError } from '@/lib/api'
import type { Category, Product, Supplier } from '@/lib/types'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Badge, Select, Toggle } from '@/components/ui/primitives'
import { formatQuantity } from '@/lib/money'
import { SearchableSelect } from '@/components/ui/SearchableSelect'
import { QuickCreate } from '@/components/ui/QuickCreate'
import { t } from '@/i18n'

export const UNITS = [
  'Each',
  'Tonne',
  'Kilogram',
  'Gram',
  'Litre',
  'Millilitre',
  'Metre',
  'Box',
  'Pack',
] as const

/** The units an item can be sold by weight in. */
const WEIGHT_UNITS = new Set(['Tonne', 'Kilogram', 'Gram'])

/**
 * What the stock unit costs you in precision.
 *
 * Quantities hold three decimals, so the unit an item is held in fixes the smallest amount that
 * can ever be sold of it. Said here, at the moment the choice is made, rather than as a rejection
 * at the till three weeks later.
 */
const SMALLEST_SALE: Record<string, string> = {
  Tonne: 'Held in tonnes, the least you can sell is 1 kg — too coarse for a shop that sells by the bag',
  Kilogram: 'Held in kilograms, you can sell down to 1 g',
  Gram: 'Held in grams, you can sell down to a milligram',
}

export interface Draft {
  sku: string
  name: string
  categoryId: number | ''
  supplierId: number | ''
  unit: string
  isWeighed: boolean
  trackExpiry: boolean
  costUsd: string
  priceUsd: string
  vatRatePercent: string
  reorderLevel: string
  allowNegativeStock: boolean
  isActive: boolean
  barcodes: string[]
}

export function toDraft(product: Product | null, defaultVatPercent: number): Draft {
  if (!product) {
    return {
      sku: '',
      name: '',
      categoryId: '',
      supplierId: '',
      unit: 'Each',
      isWeighed: false,
      trackExpiry: false,
      costUsd: '',
      priceUsd: '',
      vatRatePercent: String(defaultVatPercent),
      reorderLevel: '0',
      allowNegativeStock: false,
      isActive: true,
      barcodes: [''],
    }
  }

  return {
    sku: product.sku,
    name: product.name,
    categoryId: product.categoryId,
    supplierId: '',
    unit: product.unit,
    isWeighed: product.isWeighed,
    trackExpiry: product.trackExpiry,
    costUsd: String(product.costUsd),
    priceUsd: String(product.priceUsd),
    // Shown as a percentage because that is how a shopkeeper thinks; converted back to a
    // fraction on save, which is the only form the server accepts.
    vatRatePercent: String(product.vatRate * 100),
    reorderLevel: String(product.reorderLevel),
    allowNegativeStock: product.allowNegativeStock,
    isActive: product.isActive,
    barcodes: product.primaryBarcode ? [product.primaryBarcode] : [''],
  }
}

/**
 * Saves a draft, creating or updating.
 *
 * Shared so the modal on the inventory list and the panel inside a delivery cannot drift apart
 * about what a product is — two forms writing slightly different bodies is how one screen ends up
 * quietly unable to set a field the other can.
 */
export function useSaveProduct(product: Product | null) {
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: (draft: Draft) => {
      const body = {
        sku: draft.sku.trim(),
        name: draft.name.trim(),
        categoryId: Number(draft.categoryId),
        supplierId: draft.supplierId === '' ? null : Number(draft.supplierId),
        unit: draft.unit,
        isWeighed: draft.isWeighed,
        trackExpiry: draft.trackExpiry,
        costUsd: Number(draft.costUsd || 0),
        priceUsd: Number(draft.priceUsd || 0),
        vatRate: Number(draft.vatRatePercent || 0) / 100,
        reorderLevel: Number(draft.reorderLevel || 0),
        allowNegativeStock: draft.allowNegativeStock,
        isActive: draft.isActive,
        barcodes: draft.barcodes.map((code) => code.trim()).filter(Boolean),
      }

      return product
        ? apiClient.put<{ id: number; name: string }>(`/api/products/${product.id}`, body)
        : apiClient.post<{ id: number; name: string }>('/api/products', body)
    },
    onSuccess: async () => {
      setError(null)
      await queryClient.invalidateQueries({ queryKey: ['products'] })
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? (caught.detail ?? caught.message) : 'Could not save.'),
  })

  return { save, error, setError }
}

/** A batch holding this product, as the editor needs it. */
export interface ProductBatch {
  id: number
  batchNumber: string | null
  expiryDate: string | null
  quantityOnHand: number
}

/**
 * Everything a product is, as fields.
 *
 * Deliberately owns no chrome of its own — no modal, no heading, no save button. It is dropped
 * into a dialog on the inventory screen and straight into the page on the delivery screen, and
 * the delivery screen is the one that matters: keying a new product there used to mean a dialog
 * opening on top of the half-filled delivery behind it.
 */
export function ProductFormFields({
  draft,
  onChange,
  vatEnabled,
  productId,
  expiryEdits,
  onExpiryChange,
}: {
  draft: Draft
  onChange: (draft: Draft) => void
  vatEnabled: boolean
  /** Set when editing an existing product, so its batches can be shown. */
  productId?: number
  /** Dates the user has changed, by batch id, kept by the caller so Save can write them. */
  expiryEdits?: Record<number, string>
  onExpiryChange?: (batchId: number, date: string) => void
}) {
  const [isAddingCategory, setAddingCategory] = useState(false)

  // Only for an existing product: a new one has no stock yet, so it has nothing to expire.
  const batches = useQuery({
    queryKey: ['stock', 'batches', productId],
    queryFn: () => apiClient.get<ProductBatch[]>(`/api/stock/batches/${productId}`),
    enabled: productId !== undefined,
  })

  const categories = useQuery({
    queryKey: ['categories'],
    queryFn: () => apiClient.get<Category[]>('/api/categories'),
  })

  const suppliers = useQuery({
    queryKey: ['suppliers'],
    queryFn: () => apiClient.get<Supplier[]>('/api/suppliers'),
  })

  const margin =
    Number(draft.priceUsd) > 0 && Number(draft.costUsd) > 0
      ? ((Number(draft.priceUsd) - Number(draft.costUsd)) / Number(draft.priceUsd)) * 100
      : null

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          label={t('Name')}
          value={draft.name}
          onChange={(e) => onChange({ ...draft, name: e.target.value })}
          placeholder={t('Laban 1kg')}
          required
        />
        <Input
          label={t('SKU')}
          value={draft.sku}
          onChange={(e) => onChange({ ...draft, sku: e.target.value.toUpperCase() })}
          placeholder="LAB1KG"
          className="tabular"
          required
        />

        {/* The category a new product belongs to is very often one the shop has not made yet,
            and that is the moment it is being thought about — so it is created here, beside the
            list it was missing from, rather than on a settings screen somewhere else. */}
        <div className="flex items-end gap-2">
          <div className="min-w-0 flex-1">
            <SearchableSelect
              label={t('Category')}
              value={draft.categoryId}
              placeholder={t('Choose a category…')}
              options={(categories.data ?? []).map((category) => ({
                value: category.id,
                label: category.name,
              }))}
              onChange={(picked) => onChange({ ...draft, categoryId: Number(picked) })}
            />
          </div>
          <Button
            variant="secondary"
            size="icon"
            aria-label={t('New category')}
            title={t('New category')}
            onClick={() => setAddingCategory(true)}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>

        <SearchableSelect
          label={t('Supplier')}
          value={draft.supplierId}
          placeholder={t('No default supplier')}
          options={(suppliers.data ?? []).map((supplier) => ({
            value: supplier.id,
            label: supplier.name,
          }))}
          onChange={(picked) =>
            onChange({ ...draft, supplierId: picked === '' ? '' : Number(picked) })
          }
        />
        <Select
          label={t('Unit')}
          value={draft.unit}
          onChange={(e) => onChange({ ...draft, unit: e.target.value })}
        >
          {UNITS.map((unit) => (
            <option key={unit} value={unit}>
              {unit}
            </option>
          ))}
        </Select>

        {/* Read-only, and absent until the goods have actually been bought. The cost is the
            weighted average of the stock on hand, written by the server when a delivery is posted;
            a box to type in would be a number nobody paid, overwritten by the next delivery. The
            selling price below stays the shopkeeper's to set. */}
        {Number(draft.costUsd) > 0 ? (
          <Input
            label={t('Cost (USD)')}
            value={draft.costUsd}
            readOnly
            disabled
            className="tabular"
            hint={t('The average of what you paid for the stock on hand')}
          />
        ) : (
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">{t('Cost (USD)')}</span>
            <p className="surface-sunken rounded-lg px-3 py-2.5 text-sm text-[var(--text-secondary)]">
              {t('Set by your first delivery')}
            </p>
          </div>
        )}
        <Input
          label={t('Selling price (USD)')}
          type="number"
          inputMode="decimal"
          step="0.01"
          min="0"
          value={draft.priceUsd}
          onChange={(e) => onChange({ ...draft, priceUsd: e.target.value })}
          className="tabular"
          hint={margin === null ? undefined : `Margin ${margin.toFixed(1)}%`}
        />

        {/* Hidden when the shop is not VAT-registered — the rate stays on the record, so
            switching VAT back on restores it rather than losing which goods were exempt. */}
        {vatEnabled && (
          <Input
            label={t('VAT rate (%)')}
            type="number"
            inputMode="decimal"
            step="0.5"
            min="0"
            max="99"
            value={draft.vatRatePercent}
            onChange={(e) => onChange({ ...draft, vatRatePercent: e.target.value })}
            className="tabular"
            hint={t('0 for exempt goods like bread')}
          />
        )}
        <Input
          label={t('Reorder level')}
          type="number"
          inputMode="decimal"
          min="0"
          value={draft.reorderLevel}
          onChange={(e) => onChange({ ...draft, reorderLevel: e.target.value })}
          className="tabular"
          hint={t('Alert when stock drops to this')}
        />

        {/* Expiry belongs to a batch, not to a product — two deliveries of the same yoghurt go
            off on different days, and the till sells the soonest first. So this edits the stock
            actually on the shelf rather than pretending a product has one date. */}
        {productId !== undefined && (
          <div>
            <p className="mb-1.5 text-sm font-medium">{t('Expiry')}</p>

            {batches.data?.length ? (
              <div className="flex flex-col gap-2">
                {batches.data.map((batch) => (
                  <div key={batch.id} className="flex items-center gap-2">
                    <Input
                      type="date"
                      value={expiryEdits?.[batch.id] ?? (batch.expiryDate?.slice(0, 10) ?? '')}
                      onChange={(event) => onExpiryChange?.(batch.id, event.target.value)}
                      wrapperClassName="flex-1"
                      className="tabular"
                    />
                    <Badge tone="neutral">
                      {formatQuantity(batch.quantityOnHand)}
                      {batch.batchNumber ? ` · ${batch.batchNumber}` : ''}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="surface-sunken rounded-lg px-3 py-2 text-xs text-[var(--text-secondary)]">
                {batches.isLoading
                  ? 'Loading…'
                  : 'Nothing in stock, so there is no date to set. Expiry is entered on the delivery that brings stock in.'}
              </p>
            )}

            {(batches.data?.length ?? 0) > 0 && (
              <p className="mt-1.5 text-xs text-[var(--text-secondary)]">
                {t('One date per batch on the shelf, soonest sold first. Blank means it does not go off.')}
              </p>
            )}
          </div>
        )}
      </div>

      <div className="mt-5">
        <p className="mb-2 text-sm font-medium">{t('Barcodes')}</p>
        <div className="flex flex-col gap-2">
          {draft.barcodes.map((code, index) => (
            <div key={index} className="flex gap-2">
              <Input
                value={code}
                onChange={(e) => {
                  const next = [...draft.barcodes]
                  next[index] = e.target.value
                  onChange({ ...draft, barcodes: next })
                }}
                placeholder={t('Scan or type a barcode')}
                className="tabular flex-1"
                inputMode="numeric"
              />
              <Button
                variant="ghost"
                size="icon"
                aria-label={t('Remove barcode')}
                onClick={() =>
                  onChange({ ...draft, barcodes: draft.barcodes.filter((_, i) => i !== index) })
                }
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onChange({ ...draft, barcodes: [...draft.barcodes, ''] })}
          >
            <Plus className="h-4 w-4" />
            {t('Add another barcode')}
          </Button>
        </div>
        <p className="mt-2 text-xs text-[var(--text-secondary)]">
          Leave blank and one can be minted after the delivery is posted. The first is the primary
          code — a product can carry several: the manufacturer's, a case barcode, your own label.
        </p>
      </div>

      <div className="mt-5 flex flex-col gap-1">
        <Toggle
          checked={draft.trackExpiry}
          onChange={(v) => onChange({ ...draft, trackExpiry: v })}
          label={t('Track expiry dates')}
          hint={t('Deliveries will require an expiry date, and this appears in expiry alerts')}
        />
        <Toggle
          checked={draft.isWeighed}
          onChange={(v) => onChange({ ...draft, isWeighed: v })}
          label={t('Sold by weight')}
          hint={
            WEIGHT_UNITS.has(draft.unit)
              ? `The till asks for a weight, or an amount to weigh out, before this reaches the cart. ${SMALLEST_SALE[draft.unit]}`
              : // Turning it on against a unit that carries no weight does nothing, so say so
                // rather than leaving a switch that silently has no effect.
                'Only applies to an item measured in tonnes, kilograms or grams — set the unit above first'
          }
        />
        <Toggle
          checked={draft.allowNegativeStock}
          onChange={(v) => onChange({ ...draft, allowNegativeStock: v })}
          label={t('Allow selling below zero stock')}
          hint={t('For items whose counts are habitually loose, like loose produce')}
        />
        <Toggle
          checked={draft.isActive}
          onChange={(v) => onChange({ ...draft, isActive: v })}
          label={t('Active')}
          hint={t('Inactive products stay in history but cannot be sold')}
        />
      </div>

      <QuickCreate
        open={isAddingCategory}
        what="category"
        path="/api/categories"
        invalidates="categories"
        onClose={() => setAddingCategory(false)}
        onCreated={(created) => {
          // Selected as well as created. Making the category and then having to find it in the
          // list you just added it to is the kind of small tax that adds up all day.
          onChange({ ...draft, categoryId: created.id })
          setAddingCategory(false)
        }}
      />
    </>
  )
}
